<template>
  <AdminShell
    :page-title="pageTitle"
    :page-subtitle="pageSubtitle"
    active-menu="home"
  >
    <template #actions>
      <button class="home-dashboard__create-button" type="button" @click="goToCreate">
        创建新直播
      </button>
    </template>

    <section class="home-dashboard">
      <div v-if="loading" class="home-dashboard__feedback">加载中...</div>

      <div v-else-if="cards.length === 0" class="home-dashboard__empty">
        <div class="home-dashboard__empty-title">暂无直播场次</div>
        <div class="home-dashboard__empty-text">当前还没有可展示的直播数据，请先创建直播。</div>
      </div>

      <div v-else class="home-dashboard__grid">
        <article
          v-for="room in cards"
          :key="room.stream_name"
          class="home-dashboard__card"
        >
          <div class="home-dashboard__card-cover">
            <img :src="room.cover" :alt="room.displayTitle" class="home-dashboard__card-image" />
            <span
              class="home-dashboard__card-badge"
              :class="`is-${room.statusKey}`"
            >
              {{ room.statusText }}
            </span>

            <div class="home-dashboard__card-toolbar">
              <button
                class="home-dashboard__watch-button"
                type="button"
                @click="goToWatch(room)"
              >
                <i class="el-icon-link"></i>
                <span>查看直播</span>
              </button>
              <button
                class="home-dashboard__qr-button"
                type="button"
                :disabled="!qrUrl"
                @click="openQr(room)"
              >
                <i class="el-icon-s-grid"></i>
              </button>
            </div>
          </div>

          <div class="home-dashboard__card-body">
            <h3 class="home-dashboard__card-title">{{ room.displayTitle }}</h3>

            <div class="home-dashboard__meta">
              <div class="home-dashboard__meta-row">
                <span class="home-dashboard__meta-label">
                  <i class="el-icon-time"></i>
                  开始时间
                </span>
                <span class="home-dashboard__meta-value">{{ room.displayTime }}</span>
              </div>
              <div class="home-dashboard__meta-row">
                <span class="home-dashboard__meta-label">
                  <i class="el-icon-user"></i>
                  观看人数
                </span>
                <span class="home-dashboard__meta-value">{{ room.viewerText }}</span>
              </div>
            </div>

            <button
              class="home-dashboard__report-button"
              type="button"
              @click="goToReport(room)"
            >
              查看详细报告
            </button>
          </div>
        </article>
      </div>
    </section>

    <QrCodeDialog
      :visible.sync="showQr"
      :title="qrDialogTitle"
      :url="qrTargetUrl"
      :size="320"
      :file-name="qrDialogFileName"
    />
  </AdminShell>
</template>

<script>
import { liveroomsList, getAppid } from '@/api'
import AdminShell from '@/components/admin/AdminShell.vue'
import QrCodeDialog from '@/components/QrCodeDialog.vue'

const PAGE_TITLE = '\u76f4\u64ad\u573a\u6b21\u6570\u636e'
const PAGE_SUBTITLE = '\u7ba1\u7406\u5e76\u76d1\u63a7\u60a8\u6700\u8fd1\u7684\u76f4\u64ad\u52a8\u6001'
const LIVE_APP_URL = 'https://apps.apple.com/us/app/woklive/id6762117449'

export default {
  name: 'Home',
  components: {
    AdminShell,
    QrCodeDialog,
  },
  data() {
    return {
      liveRooms: [],
      loading: false,
      appid: 0,
      pageTitle: PAGE_TITLE,
      pageSubtitle: PAGE_SUBTITLE,
      qrUrl: '',
      showQr: false,
      activeQrRoom: null,
      pageSize: 60,
    }
  },
  computed: {
    cards() {
      return this.liveRooms.map((roomInfo) => {
        const status = Number(roomInfo.status)
        const viewerCount = this.resolveViewerCount(roomInfo)
        return {
          ...roomInfo,
          // cover: this.resolveCover(roomInfo),
          cover: '',
          displayTitle: roomInfo.live_title || roomInfo.stream_name || '未命名直播间',
          displayTime: this.formatDisplayTime(roomInfo.time_on || roomInfo.created_at),
          statusText: this.getStatusText(status),
          statusKey: this.getStatusKey(status),
          viewerText: Number(viewerCount || 0).toLocaleString(),
        }
      })
    },
    qrDialogTitle() {
      const room = this.activeQrRoom
      if (!room) return '直播间二维码'
      return `${room.live_title || room.stream_name || '直播间'}二维码`
    },
    qrDialogFileName() {
      const room = this.activeQrRoom
      if (!room || !room.stream_name) return 'wokfoy-live_qrcode'
      return `wokfoy-live_${room.stream_name}`
    },
    qrTargetUrl() {
      const room = this.activeQrRoom
      if (!room || !this.qrUrl) return this.qrUrl
      const base = /^https?:\/\//i.test(this.qrUrl) ? this.qrUrl : `https://${this.qrUrl}`
      const cleanBase = base.replace(/\/+$/, '')
      const separator = cleanBase.includes('?') ? '&' : '?'
      return `${cleanBase}${separator}room=${encodeURIComponent(room.stream_name || '')}`
    },
  },
  async mounted() {
    await this.initPage()
  },
  methods: {
    async initPage() {
      this.loading = true
      try {
        const { data: apps } = await getAppid()
        const currentApp = Array.isArray(apps) ? apps[0] : null
        if (!currentApp) return

        this.qrUrl = currentApp.live_domain || ''
        this.appid = currentApp.id

        const { data } = await liveroomsList(this.appid, this.pageSize, 1)
        this.liveRooms = Array.isArray(data.data) ? data.data : []
      } finally {
        this.loading = false
      }
    },
    getStatusText(status) {
      if (status === 1) return '正在直播'
      if (status === 2) return '已结束'
      return '未开始'
    },
    getStatusKey(status) {
      if (status === 1) return 'live'
      if (status === 2) return 'ended'
      return 'idle'
    },
    resolveViewerCount(roomInfo) {
      const candidates = [roomInfo.online_count, roomInfo.viewer_count, roomInfo.visit_count, 0]
      const value = candidates.find((item) => item !== undefined && item !== null && item !== '')
      const numeric = Number(value)
      return Number.isFinite(numeric) ? numeric : 0
    },
    resolveCover(roomInfo) {
      return [roomInfo.image, roomInfo.cover, roomInfo.poster, roomInfo.thumb, '']
        .find((item) => item !== undefined && item !== null && item !== '')
    },
    formatDisplayTime(value) {
      if (value === null || value === undefined || value === '') return '--'

      const raw = String(value).trim()
      const numeric = Number(raw)
      let date = null

      if (raw && Number.isFinite(numeric)) {
        date = new Date(raw.length === 10 ? numeric * 1000 : numeric)
      } else {
        date = new Date(raw.replace(/-/g, '/'))
      }

      if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return raw
      }

      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hour = String(date.getHours()).padStart(2, '0')
      const minute = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hour}:${minute}`
    },
    goToCreate() {
      this.$confirm(
        `
          <div style="line-height:1.8;">
            <div>请前往 APP 开播。</div>
            <a href="${LIVE_APP_URL}" target="_blank" rel="noreferrer" style="color:#315efb;word-break:break-all;">
              ${LIVE_APP_URL}
            </a>
          </div>
        `,
        'APP',
        {
          dangerouslyUseHTMLString: true,
          confirmButtonText: '打开下载页',
          cancelButtonText: '我知道了',
          type: 'info',
          distinguishCancelAndClose: true,
        }
      )
        .then(() => {
          window.open(LIVE_APP_URL, '_blank')
        })
        .catch(() => {})
    },
    goToWatch(roomInfo) {
      this.$router.push({
        path: '/BigScreen',
        query: {
          room: roomInfo.stream_name || '',
        },
      })
    },
    goToReport(roomInfo) {
      this.$router.push({
        path: '/live-overview',
        query: {
          room_id: roomInfo.stream_name || '',
        },
      })
    },
    openQr(roomInfo) {
      this.activeQrRoom = roomInfo
      this.showQr = true
    },
  },
}
</script>

<style scoped>
.home-dashboard__create-button {
  min-width: 160px;
  height: 50px;
  padding: 0 28px;
  border: 0;
  border-radius: 14px;
  background: linear-gradient(180deg, #3a7bff 0%, #2f5fe4 100%);
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 16px 34px rgba(47, 95, 228, 0.22);
}

.home-dashboard {
  min-height: 420px;
}

.home-dashboard__feedback,
.home-dashboard__empty {
  min-height: 360px;
  display: grid;
  place-items: center;
  border: 1px solid #dde4f2;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.72);
  color: #667792;
}

.home-dashboard__empty {
  place-items: center;
  align-content: center;
  gap: 10px;
}

.home-dashboard__empty-title {
  color: #12203f;
  font-size: 22px;
  font-weight: 700;
}

.home-dashboard__empty-text {
  font-size: 14px;
}

.home-dashboard__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px;
}

.home-dashboard__card {
  overflow: hidden;
  border: 1px solid #dbe2ee;
  border-radius: 26px;
  background: #fff;
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.06);
}

.home-dashboard__card-cover {
  position: relative;
  height: 240px;
  background: #0f172a;
}

.home-dashboard__card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.home-dashboard__card-badge {
  position: absolute;
  top: 18px;
  left: 22px;
  min-width: 78px;
  height: 32px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
}

.home-dashboard__card-badge.is-live {
  background: #ff5451;
}

.home-dashboard__card-badge.is-idle {
  background: #4689ff;
}

.home-dashboard__card-badge.is-ended {
  background: #6e7e99;
}

.home-dashboard__card-toolbar {
  position: absolute;
  left: 20px;
  right: 20px;
  bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.home-dashboard__watch-button,
.home-dashboard__qr-button,
.home-dashboard__report-button {
  border: 1px solid #dbe2ee;
  background: #fff;
  color: #12203f;
  cursor: pointer;
}

.home-dashboard__watch-button {
  flex: 1;
  min-height: 48px;
  padding: 0 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 700;
}

.home-dashboard__qr-button {
  width: 52px;
  height: 48px;
  border-radius: 14px;
  font-size: 18px;
}

.home-dashboard__qr-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.home-dashboard__card-body {
  padding: 24px 24px 26px;
}

.home-dashboard__card-title {
  min-height: 68px;
  margin: 0;
  color: #12203f;
  font-size: 22px;
  line-height: 1.55;
  font-weight: 800;
}

.home-dashboard__meta {
  margin-top: 14px;
  display: grid;
  gap: 10px;
}

.home-dashboard__meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: #64748b;
  font-size: 14px;
}

.home-dashboard__meta-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.home-dashboard__meta-value {
  color: #12203f;
  font-size: 16px;
}

.home-dashboard__report-button {
  width: 100%;
  min-height: 54px;
  margin-top: 24px;
  border-radius: 14px;
  font-size: 16px;
}

@media (max-width: 1280px) {
  .home-dashboard__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .home-dashboard__create-button {
    width: 100%;
  }

  .home-dashboard__grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .home-dashboard__card-cover {
    height: 220px;
  }

  .home-dashboard__card-body {
    padding: 20px 18px 20px;
  }

  .home-dashboard__card-title {
    min-height: 0;
    font-size: 20px;
  }

  .home-dashboard__meta-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .home-dashboard__watch-button {
    font-size: 15px;
  }

  .home-dashboard__report-button {
    min-height: 48px;
  }
}
</style>
